import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { startCoderbyteTest } from '@internal/api/coderbyte/coderbyte';
import { rolesApi } from '@internal/api/roles';
import { CandidateRoleCategory } from '@internal/api/roles/roles';
import { useApiContext } from '@internal/components/common/api-context';
import { useLoadedOnce } from '@internal/hooks/use-loaded-once';

import {
  CandidateRole,
  NOT_STARTED_ASSESSMENT,
  NOT_STARTED_INTERVIEW_BOOKING,
  NOT_STARTED_REVIEW,
  NOT_STARTED_VERIFICATION,
} from './role-types';

interface RoleContextValue {
  roles: CandidateRole[];
  loading: boolean;
  pickedRoles: CandidateRole[];
  isRolePickable: (roleId: string) => boolean;
  pickRole: (roleId: string) => Promise<boolean>;
  startTest: (roleId: string) => Promise<string | null>;
  startCall: (roleId: string) => Promise<boolean>;
  requestBookingLink: (roleId: string) => Promise<string | null>;
  refreshRoles: () => Promise<void>;
}

const RoleContext = createContext<RoleContextValue | null>(null);

const parseLabel = (label: string): { title: string; stack: string } => {
  const dashIndex = label.indexOf('—');
  if (dashIndex === -1) return { title: label, stack: '' };
  return {
    title: label.slice(0, dashIndex).trim(),
    stack: label.slice(dashIndex + 1).trim(),
  };
};

const TEST_RESULTS_POLL_INTERVAL_MS = 10_000;

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { profile } = useApiContext();
  const apiKey = profile?.apiKey ?? '';
  const queryClient = useQueryClient();

  const { candidateRoles, candidateRolesIsLoading } =
    rolesApi.getCandidateRoles.useQuery(apiKey, {
      enabled: Boolean(apiKey),
      refetchInterval: query =>
        query.state.data?.hasActiveTest ? TEST_RESULTS_POLL_INTERVAL_MS : false,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: query =>
        query.state.data?.hasActiveTest ? 'always' : false,
    });
  const { addRoleAsync } = rolesApi.addRole.useMutation();
  const { startCoderbyteTestAsync } = startCoderbyteTest.useMutation();
  const { createInterviewBookingLinkAsync } =
    rolesApi.createInterviewBookingLink.useMutation();

  const rolesLoaded = useLoadedOnce(!apiKey || candidateRolesIsLoading);

  const backendRoles = useMemo(
    () => candidateRoles?.roles ?? [],
    [candidateRoles?.roles]
  );

  const roles = useMemo<CandidateRole[]>(
    () =>
      backendRoles.map(item => {
        const id = item.role;
        const label = item.humanReadableRole ?? item.role;
        const assessment = item.assessment ?? NOT_STARTED_ASSESSMENT;
        const contactRoleId = item.id === item.role ? null : item.id;
        return {
          id,
          contactRoleId,
          role: item.role,
          label,
          ...parseLabel(label),
          pickable: item.pickable,
          isMain: item.isMain ?? false,
          isVerified: item.isVerified ?? false,
          roleCategory: item.roleCategory ?? CandidateRoleCategory.Other,
          paused: item.paused ?? false,
          onHold: item.onHold ?? false,
          review: item.review ?? NOT_STARTED_REVIEW,
          verification: item.verification ?? NOT_STARTED_VERIFICATION,
          assessment,
          interviewBooking:
            item.interviewBooking ?? NOT_STARTED_INTERVIEW_BOOKING,
          started: item.started ?? false,
        };
      }),
    [backendRoles]
  );

  const refreshRoles = useCallback(
    () =>
      queryClient
        .invalidateQueries({ queryKey: ['candidate-roles', apiKey] })
        .catch(() => {}),
    [apiKey, queryClient]
  );

  const pickedRoles = useMemo(
    () => roles.filter(role => role.started),
    [roles]
  );
  const isRolePickable = useCallback(
    (roleId: string) => {
      const role = roles.find(r => r.id === roleId);
      return Boolean(role?.pickable);
    },
    [roles]
  );

  const pickRole = useCallback(
    async (roleId: string): Promise<boolean> => {
      if (!isRolePickable(roleId) || !apiKey) return false;
      await addRoleAsync({ apiKey, role: roleId });
      await refreshRoles();
      return true;
    },
    [addRoleAsync, apiKey, isRolePickable, refreshRoles]
  );

  const startTest = useCallback(
    async (roleId: string): Promise<string | null> => {
      if (!isRolePickable(roleId) || !apiKey) return null;
      await addRoleAsync({ apiKey, role: roleId });
      const inviteUrl = await startCoderbyteTestAsync({
        apiKey,
        role: roleId,
      });
      refreshRoles();
      return inviteUrl;
    },
    [
      addRoleAsync,
      apiKey,
      isRolePickable,
      refreshRoles,
      startCoderbyteTestAsync,
    ]
  );

  const startCall = useCallback(
    async (roleId: string): Promise<boolean> => {
      if (!isRolePickable(roleId) || !apiKey) return false;
      await addRoleAsync({ apiKey, role: roleId });
      refreshRoles();
      return true;
    },
    [addRoleAsync, apiKey, isRolePickable, refreshRoles]
  );

  const requestBookingLink = useCallback(
    async (roleId: string): Promise<string | null> => {
      const role = roles.find(r => r.id === roleId);
      if (!apiKey || !role?.contactRoleId) return null;
      try {
        const { link } = await createInterviewBookingLinkAsync({
          apiKey,
          contactRoleId: role.contactRoleId,
        });
        return link ?? null;
      } finally {
        refreshRoles();
      }
    },
    [apiKey, createInterviewBookingLinkAsync, refreshRoles, roles]
  );

  const value = useMemo<RoleContextValue>(
    () => ({
      roles,
      loading: !rolesLoaded,
      pickedRoles,
      isRolePickable,
      pickRole,
      startTest,
      startCall,
      requestBookingLink,
      refreshRoles,
    }),
    [
      roles,
      rolesLoaded,
      pickedRoles,
      isRolePickable,
      pickRole,
      startTest,
      startCall,
      requestBookingLink,
      refreshRoles,
    ]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useCandidateRoles = (): CandidateRole[] =>
  useContext(RoleContext)?.roles ?? [];

export const useRoleContext = (): RoleContextValue => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRoleContext must be used within a RoleProvider');
  }
  return context;
};
